import { Link } from 'react-router';
import { Button } from '@monksflow/monks-ui';
import { ArrowLeftIcon, FileTextIcon } from 'lucide-react';
import {
  SectionPageHeader,
  SectionPageHeaderContent,
  SectionPageHeaderDescription,
  SectionPageHeaderIcon,
  SectionPageHeaderTitle,
} from '@/shared/ui/layout';
import { EmptyState } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { useTranslation } from 'react-i18next';

type BriefTemplateComingSoonMode = 'new' | 'detail';

export interface BriefTemplateComingSoonProps {
  /** Determines the heading copy. `new` for the create flow; `detail` for the edit flow. */
  mode: BriefTemplateComingSoonMode;
}

const COPY = {
  new: {
    title: 'knowledgeHub.briefTemplateComingSoon.createNewBriefTemplate',
    description: 'knowledgeHub.briefTemplateComingSoon.thisEditorIsComingSoon',
  },
  detail: {
    title: 'knowledgeHub.briefTemplateComingSoon.editBriefTemplate',
    description: 'knowledgeHub.briefTemplateComingSoon.thisEditorIsComingSoon',
  },
} as const satisfies Record<BriefTemplateComingSoonMode, { title: string; description: string }>;

/**
 * Placeholder page mounted at `/knowledge-hub/brief-templates/new` and
 * `/knowledge-hub/brief-templates/:id` while the actual brief template editor
 * is being built.
 *
 * A single component with `mode: 'new' | 'detail'` covers both routes: the
 * only divergence is the heading copy.
 */
export const BriefTemplateComingSoon = ({ mode }: BriefTemplateComingSoonProps) => {
  const { t } = useTranslation('knowledge');

  const { title, description } = COPY[mode];

  return (
    <div className="flex flex-col gap-4">
      <SectionPageHeader variant="compact">
        <SectionPageHeaderIcon icon={FileTextIcon} />
        <SectionPageHeaderContent>
          <SectionPageHeaderTitle>{t(title)}</SectionPageHeaderTitle>
          <SectionPageHeaderDescription>{t(description)}</SectionPageHeaderDescription>
        </SectionPageHeaderContent>
      </SectionPageHeader>

      <EmptyState
        title={t('knowledgeHub.briefTemplateComingSoon.theBriefTemplateEditorIs')}
        className="py-12"
      >
        <Button variant="outline" asChild>
          <Link to={ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATES}>
            <ArrowLeftIcon />
            {t('knowledgeHub.briefTemplateComingSoon.backToTemplatesList')}
          </Link>
        </Button>
      </EmptyState>
    </div>
  );
};

BriefTemplateComingSoon.displayName = 'BriefTemplateComingSoon';
